import React, { useEffect, useState } from "react";
import { AiOutlinePlus, AiOutlineMinus } from "react-icons/ai";
import { Box, Modal } from '@mui/material';
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { RiDeleteBin6Fill, RiEdit2Fill } from "react-icons/ri";
import { allFaqs, createFaq, updateFaq, deleteFaq } from "../Redux/Slice/faq.slice";
import { motion, AnimatePresence } from "framer-motion";
import { FaPlus, FaSearch, FaTimes, FaQuestionCircle } from "react-icons/fa";

const Faq = () => {
    const [openIndex, setOpenIndex] = useState(0);
    const [searchValue, setSearchValue] = useState('');
    const [createopen, setCreateopen] = useState(false);
    const [faqData, setFaqData] = useState("");
    const dispatch = useDispatch();
    const [delOpen, setDelOpen] = useState(false);
    const { faqs, loading, error } = useSelector(state => state.faq);

    const toggleIndex = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const validationSchema = Yup.object({
        faqQuestion: Yup.string().required("Question is required"),
        faqAnswer: Yup.string().required("Answer is required"),
    });

    const formik = useFormik({
        initialValues: { faqQuestion: "", faqAnswer: "" },
        validationSchema,
        onSubmit: async (values) => {
            if (faqData) dispatch(updateFaq({ _id: faqData._id, ...values }));
            else dispatch(createFaq(values));
            handleCreateClose();
        },
    });

    const handleCreateClose = () => {
        setCreateopen(false);
        setFaqData("");
        formik.resetForm();
    };

    const handleOpen = (data) => {
        setCreateopen(true);
        setFaqData(data);
        if (data) {
            formik.setValues({
                faqQuestion: data.faqQuestion,
                faqAnswer: data.faqAnswer,
            });
        }
    };

    useEffect(() => {
        dispatch(allFaqs());
    }, [dispatch]);

    const filteredFaqs = Array.isArray(faqs)
        ? faqs.filter(faq =>
            faq.faqQuestion.toLowerCase().includes(searchValue.toLowerCase()) ||
            faq.faqAnswer.toLowerCase().includes(searchValue.toLowerCase())
        )
        : [];

    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white tracking-tight">Support FAQs</h1>
                    <p className="text-gray-400">Curate the knowledge base for your players.</p>
                </div>
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => setCreateopen(true)} className="flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-gradient-primary text-white font-bold shadow-lg shadow-primary/20 transition-all">
                    <FaPlus /> Add Question
                </motion.button>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-3xl overflow-hidden backdrop-blur-sm">
                <div className="p-6 border-b border-white/5 flex flex-col sm:flex-row gap-4 items-center justify-between">
                    <div className="relative w-full sm:w-80 group">
                        <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-primary transition-colors" />
                        <input
                            type="text"
                            placeholder="Search questions or answers..."
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-xl pl-12 pr-4 py-2.5 text-sm text-white focus:border-primary/50 outline-none transition-all"
                        />
                    </div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                        Total Items: {filteredFaqs.length}
                    </p>
                </div>

                <div className="p-6 space-y-4">
                    {filteredFaqs.map((faq, index) => (
                        <div key={index} className="group border border-white/5 rounded-2xl overflow-hidden hover:border-primary/30 transition-all bg-white/[0.02]">
                            <div className="flex items-center">
                                <button onClick={() => toggleIndex(index)} className="flex-1 flex items-center justify-between p-5 text-left transition-colors hover:bg-white/[0.02]">
                                    <div className="flex items-center gap-5">
                                        <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-xs font-bold text-gray-500 border border-white/10 group-hover:text-primary group-hover:border-primary/30 transition-all">
                                            {String(index + 1).padStart(2, "0")}
                                        </div>
                                        <span className="text-sm md:text-base font-bold text-gray-200 group-hover:text-white transition-colors">{faq.faqQuestion}</span>
                                    </div>
                                    <div className={`p-2 rounded-lg transition-all ${openIndex === index ? 'bg-primary text-white rotate-180' : 'bg-white/5 text-gray-500 group-hover:text-white'}`}>
                                        {openIndex === index ? <AiOutlineMinus /> : <AiOutlinePlus />}
                                    </div>
                                </button>
                                <div className="pr-5 flex gap-2">
                                    <button onClick={() => handleOpen(faq)} className="p-2 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500 hover:text-white transition-all border border-green-500/20 shadow-lg shadow-green-500/5"><RiEdit2Fill size={16} /></button>
                                    <button onClick={() => { setFaqData(faq); setDelOpen(true); }} className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white transition-all border border-red-500/20 shadow-lg shadow-red-500/5"><RiDeleteBin6Fill size={16} /></button>
                                </div>
                            </div>
                            
                            <AnimatePresence>
                                {openIndex === index && (
                                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                                        <div className="px-5 pb-6 pt-0 ml-[60px] max-w-3xl">
                                            <div className="h-px bg-white/5 mb-4 w-full" />
                                            <p className="text-gray-400 text-sm leading-relaxed italic border-l-2 border-primary/30 pl-4 bg-primary/5 py-3 rounded-r-xl font-medium">
                                                {faq.faqAnswer}
                                            </p>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ))}
                    {filteredFaqs.length === 0 && <div className="text-center py-12 text-gray-500 italic">No FAQs found matching your criteria.</div>}
                </div>
            </div>

            <Modal open={createopen} onClose={handleCreateClose} className="flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-[#141414] border border-white/10 rounded-[40px] w-full max-w-lg shadow-2xl outline-none overflow-hidden">
                    <div className="p-8 border-b border-white/5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center border border-primary/20 shadow-lg shadow-primary/5">
                                <FaQuestionCircle size={24} />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-white">{faqData ? "Update FAQ" : "New Question"}</h2>
                                <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest">Support Architecture</p>
                            </div>
                        </div>
                        <button onClick={handleCreateClose} className="p-3 rounded-2xl bg-white/5 text-gray-400 hover:text-white transition-all border border-white/10">
                            <FaTimes size={20} />
                        </button>
                    </div>

                    <form onSubmit={formik.handleSubmit} className="p-8 space-y-6">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase text-gray-500 tracking-widest ml-1">The Question</label>
                            <input name="faqQuestion" type="text" placeholder="How do I reset my license key?" className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white focus:border-primary outline-none transition-all" {...formik.getFieldProps('faqQuestion')} />
                            {formik.touched.faqQuestion && formik.errors.faqQuestion && <p className="text-red-400 text-[10px] ml-1">{formik.errors.faqQuestion}</p>}
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase text-gray-500 tracking-widest ml-1">The Answer</label>
                            <textarea name="faqAnswer" rows="5" placeholder="Provide a detailed, helpful response..." className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white focus:border-primary outline-none transition-all resize-none" {...formik.getFieldProps('faqAnswer')} />
                            {formik.touched.faqAnswer && formik.errors.faqAnswer && <p className="text-red-400 text-[10px] ml-1">{formik.errors.faqAnswer}</p>}
                        </div>

                        <div className="pt-4 flex gap-3">
                            <button type="button" onClick={handleCreateClose} className="flex-1 py-4 rounded-[20px] border border-white/10 text-white font-bold hover:bg-white/5">Cancel</button>
                            <button type="submit" className="flex-[2] py-4 rounded-[20px] bg-gradient-primary text-white font-bold shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all">
                                {faqData ? 'Update Knowledge' : 'Publish Question'}
                            </button>
                        </div>
                    </form>
                </motion.div>
            </Modal>

            <Modal open={delOpen} onClose={() => setDelOpen(false)} className="flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-[#141414] border border-white/10 rounded-[32px] p-10 text-center w-full max-w-sm shadow-2xl">
                    <div className="w-20 h-20 rounded-3xl bg-red-500/10 text-red-500 flex items-center justify-center mx-auto mb-8 text-3xl border border-red-500/20">
                        <RiDeleteBin6Fill />
                    </div>
                    <h2 className="text-2xl font-bold mb-3 text-white">Discard Question?</h2>
                    <p className="text-gray-400 text-sm mb-10 leading-relaxed">Are you sure you want to remove <span className="text-white font-bold">"{faqData?.faqQuestion}"</span>? This will be immediately purged from the user side.</p>
                    <div className="flex gap-4">
                        <button onClick={() => setDelOpen(false)} className="flex-1 py-4 rounded-2xl bg-white/5 hover:bg-white/10 transition-colors border border-white/10 text-white font-bold">Cancel</button>
                        <button onClick={() => { dispatch(deleteFaq({ _id: faqData._id })); setDelOpen(false); }} className="flex-1 py-4 rounded-2xl bg-red-500 text-white font-bold shadow-[0_0_30px_rgba(239,68,68,0.3)]">Delete</button>
                    </div>
                </motion.div>
            </Modal>
        </motion.div>
    );
};

export default Faq;
